import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Tag, MapPin, Plus, Edit2, Trash2, X, Check, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';

export default function ManagerSettings() {
  const API_URL = process.env.REACT_APP_API_URL;
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const token = storedUser?.token;
  const navigate = useNavigate();

  // States
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);

  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: '', color: '#3B82F6', description: '' });
  const [locationForm, setLocationForm] = useState({ name: '' });

  // Filter states
  const [showInactiveCategories, setShowInactiveCategories] = useState(false);
  const [showInactiveLocations, setShowInactiveLocations] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchLocations();
  }, []);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Fetch functions
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await authFetch(`${API_URL}/api/manager/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      setCategories(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await authFetch(`${API_URL}/api/manager/locations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch locations');
      const data = await response.json();
      setLocations(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Category CRUD
  const handleSaveCategory = async () => {
    try {
      setLoading(true);
      const url = editingCategory
        ? `${API_URL}/api/manager/categories/${editingCategory.id}`
        : `${API_URL}/api/manager/categories`;
      
      const method = editingCategory ? 'PUT' : 'POST';
      
      const response = await authFetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(categoryForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save category');
      }

      setSuccess(editingCategory ? 'Category updated successfully' : 'Category created successfully');
      setShowCategoryModal(false);
      setCategoryForm({ name: '', color: '#3B82F6', description: '' });
      setEditingCategory(null);
      fetchCategories();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCategoryActive = async (category) => {
    try {
      setLoading(true);
      const response = await authFetch(
        `${API_URL}/api/manager/categories/${category.id}/toggle-active`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ active: !category.active })
        }
      );

      if (!response.ok) throw new Error('Failed to toggle category status');

      setSuccess(`Category ${category.active ? 'deactivated' : 'activated'} successfully`);
      fetchCategories();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Location CRUD
  const handleSaveLocation = async () => {
    try {
      setLoading(true);
      const url = editingLocation
        ? `${API_URL}/api/manager/locations/${editingLocation.id}`
        : `${API_URL}/api/manager/locations`;
      
      const method = editingLocation ? 'PUT' : 'POST';
      
      const response = await authFetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(locationForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save location');
      }

      setSuccess(editingLocation ? 'Location updated successfully' : 'Location created successfully');
      setShowLocationModal(false);
      setLocationForm({ name: '' });
      setEditingLocation(null);
      fetchLocations();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLocationActive = async (location) => {
    try {
      setLoading(true);
      const response = await authFetch(
        `${API_URL}/api/manager/locations/${location.id}/toggle-active`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ active: !location.active })
        }
      );

      if (!response.ok) throw new Error('Failed to toggle location status');

      setSuccess(`Location ${location.active ? 'deactivated' : 'activated'} successfully`);
      fetchLocations();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Modal handlers
  const openCategoryModal = (category = null) => {
    if (category) {
      setCategoryForm({
        name: category.name,
        color: category.color,
        description: category.description || ''
      });
      setEditingCategory(category);
    } else {
      setCategoryForm({ name: '', color: '#3B82F6', description: '' });
      setEditingCategory(null);
    }
    setShowCategoryModal(true);
  };

  const openLocationModal = (location = null) => {
    if (location) {
      setLocationForm({ name: location.name });
      setEditingLocation(location);
    } else {
      setLocationForm({ name: '' });
      setEditingLocation(null);
    }
    setShowLocationModal(true);
  };

  const closeModals = () => {
    setShowCategoryModal(false);
    setShowLocationModal(false);
    setCategoryForm({ name: '', color: '#3B82F6', description: '' });
    setLocationForm({ name: '' });
    setEditingCategory(null);
    setEditingLocation(null);
  };

  // Filter categories and locations
  const filteredCategories = showInactiveCategories 
    ? categories 
    : categories.filter(cat => cat.active);

  const filteredLocations = showInactiveLocations 
    ? locations 
    : locations.filter(loc => loc.active);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/manager-dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-6 h-6 text-blue-900" />
              </button>
              <Settings className="w-8 h-8 text-blue-900" />
              <div>
                <h1 className="text-3xl font-bold text-blue-900">Settings</h1>
                <p className="text-gray-600">Manage work categories and locations</p>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)}><X className="w-5 h-5" /></button>
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-center justify-between">
            <span>{success}</span>
            <button onClick={() => setSuccess(null)}><X className="w-5 h-5" /></button>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          
          {/* CATEGORIES SECTION */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Tag className="w-6 h-6 text-yellow-500" />
                <h2 className="text-2xl font-bold text-blue-900">Work Categories</h2>
              </div>
              <button
                onClick={() => openCategoryModal()}
                className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg transition-colors font-semibold"
              >
                <Plus className="w-5 h-5" />
                Add Category
              </button>
            </div>

            {/* Toggle inactive */}
            <div className="mb-4 flex items-center gap-2">
              <button
                onClick={() => setShowInactiveCategories(!showInactiveCategories)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-900 transition-colors"
              >
                {showInactiveCategories ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showInactiveCategories ? 'Hide' : 'Show'} inactive categories
              </button>
            </div>

            {/* Categories List */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {loading && filteredCategories.length === 0 ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto"></div>
                </div>
              ) : filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <div
                    key={category.id}
                    className={`border-l-4 p-4 rounded-r-lg transition-all ${
                      category.active 
                        ? 'bg-gray-50 hover:shadow-md' 
                        : 'bg-gray-100 opacity-60'
                    }`}
                    style={{ borderColor: category.color }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div
                          className="w-8 h-8 rounded-full"
                          style={{ backgroundColor: category.color }}
                        ></div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-blue-900">{category.name}</h3>
                            {!category.active && (
                              <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full font-semibold">
                                Inactive
                              </span>
                            )}
                          </div>
                          {category.description && (
                            <p className="text-sm text-gray-600">{category.description}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Used in {category.usageCount || 0} work entries
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openCategoryModal(category)}
                          className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4 text-blue-900" />
                        </button>
                        <button
                          onClick={() => handleToggleCategoryActive(category)}
                          className={`p-2 rounded-lg transition-colors ${
                            category.active
                              ? 'hover:bg-red-100 text-red-600'
                              : 'hover:bg-green-100 text-green-600'
                          }`}
                          title={category.active ? 'Deactivate' : 'Activate'}
                        >
                          {category.active ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <Tag className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No categories yet</p>
                </div>
              )}
            </div>
          </div>

          {/* LOCATIONS SECTION */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <MapPin className="w-6 h-6 text-blue-500" />
                <h2 className="text-2xl font-bold text-blue-900">Locations</h2>
              </div>
              <button
                onClick={() => openLocationModal()}
                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors font-semibold"
              >
                <Plus className="w-5 h-5" />
                Add Location
              </button>
            </div>

            {/* Toggle inactive */}
            <div className="mb-4 flex items-center gap-2">
              <button
                onClick={() => setShowInactiveLocations(!showInactiveLocations)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-900 transition-colors"
              >
                {showInactiveLocations ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showInactiveLocations ? 'Hide' : 'Show'} inactive locations
              </button>
            </div>

            {/* Locations List */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {loading && filteredLocations.length === 0 ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto"></div>
                </div>
              ) : filteredLocations.length > 0 ? (
                filteredLocations.map((location) => (
                  <div
                    key={location.id}
                    className={`border-l-4 border-blue-500 p-4 rounded-r-lg transition-all ${
                      location.active 
                        ? 'bg-gray-50 hover:shadow-md' 
                        : 'bg-gray-100 opacity-60'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <MapPin className="w-6 h-6 text-blue-500" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-blue-900">{location.name}</h3>
                            {!location.active && (
                              <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full font-semibold">
                                Inactive
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Used in {location.usageCount || 0} work entries
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openLocationModal(location)}
                          className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4 text-blue-900" />
                        </button>
                        <button
                          onClick={() => handleToggleLocationActive(location)}
                          className={`p-2 rounded-lg transition-colors ${
                            location.active
                              ? 'hover:bg-red-100 text-red-600'
                              : 'hover:bg-green-100 text-green-600'
                          }`}
                          title={location.active ? 'Deactivate' : 'Activate'}
                        >
                          {location.active ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No locations yet</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CATEGORY MODAL */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-blue-900">
                  {editingCategory ? 'Edit Category' : 'Add Category'}
                </h3>
                <button onClick={closeModals} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                    placeholder="e.g., Plumbing, Electrical..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Color *
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={categoryForm.color}
                      onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                      className="w-16 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={categoryForm.color}
                      onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                    rows="3"
                    placeholder="Brief description..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleSaveCategory}
                    disabled={!categoryForm.name || !categoryForm.color || loading}
                    className="flex-1 flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Check className="w-5 h-5" />
                    {editingCategory ? 'Update' : 'Create'}
                  </button>
                  <button
                    onClick={closeModals}
                    className="flex-1 border border-gray-300 hover:bg-gray-100 text-gray-800 px-4 py-2 rounded-lg transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* LOCATION MODAL */}
        {showLocationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-blue-900">
                  {editingLocation ? 'Edit Location' : 'Add Location'}
                </h3>
                <button onClick={closeModals} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Location Name *
                  </label>
                  <input
                    type="text"
                    value={locationForm.name}
                    onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
                    placeholder="e.g., Site A - Building 1..."
                    maxLength={256}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {locationForm.name.length}/256 characters
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleSaveLocation}
                    disabled={!locationForm.name || loading}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Check className="w-5 h-5" />
                    {editingLocation ? 'Update' : 'Create'}
                  </button>
                  <button
                    onClick={closeModals}
                    className="flex-1 border border-gray-300 hover:bg-gray-100 text-gray-800 px-4 py-2 rounded-lg transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}